/**
 * Utilitaires spécifiques aux domaines Gandi
 */

/**
 * Parse rawData Gandi (objet ou chaîne JSON)
 * @param {Object|string|null|undefined} rawData
 * @returns {Object|null}
 */
export const parseGandiRawData = (rawData) => {
  if (!rawData) return null;
  if (typeof rawData === 'string') {
    try {
      return JSON.parse(rawData);
    } catch (e) {
      return null;
    }
  }
  return rawData;
};

/**
 * Date d'expiration d'un domaine Gandi
 * @param {Object} domainInfo
 * @returns {string|null}
 */
export const getGandiExpirationDate = (domainInfo) => {
  if (!domainInfo) return null;

  const parsedRawData = parseGandiRawData(domainInfo.rawData);

  if (parsedRawData?.basic?.dates) {
    return parsedRawData.basic.dates.registry_ends_at;
  }
  if (domainInfo.dateRegistryEnd) {
    return domainInfo.dateRegistryEnd;
  }

  return null;
};

/**
 * Statut dérivé d'un domaine Gandi
 * @param {Object} domainInfo
 * @returns {'active'|'expiring'|'expired'|'unknown'}
 */
export const getGandiDomainStatus = (domainInfo) => {
  if (!domainInfo) return 'unknown';

  const status = domainInfo.status;
  const expirationDate = getGandiExpirationDate(domainInfo);
  const now = new Date();

  const statusFromExpiration = () => {
    if (!expirationDate) return null;
    const daysUntilExpiration = Math.ceil(
      (new Date(expirationDate) - now) / (1000 * 60 * 60 * 24)
    );
    if (daysUntilExpiration < 0) return 'expired';
    if (daysUntilExpiration < 30) return 'expiring';
    return 'active';
  };

  if (status && Array.isArray(status) && status.length > 0) {
    return statusFromExpiration() || 'active';
  }

  return statusFromExpiration() || 'unknown';
};

/**
 * Jours restants avant expiration (Gandi)
 * @param {Object} domainInfo
 * @returns {number|null}
 */
export const getGandiDaysUntilExpiration = (domainInfo) => {
  if (!domainInfo) return null;

  const expirationDate = getGandiExpirationDate(domainInfo);
  if (!expirationDate) return null;

  const now = new Date();
  const expDate = new Date(expirationDate);
  return Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
};

/**
 * État du renouvellement Gandi
 * @param {Object} domainInfo
 * @returns {'automatic'|'manual'|'unknown'}
 */
export const getGandiRenewalState = (domainInfo) => {
  if (!domainInfo) return 'unknown';

  if (domainInfo.rawData) {
    try {
      const rawData = parseGandiRawData(domainInfo.rawData);
      if (rawData?.basic?.autorenew?.enabled === true) {
        return 'automatic';
      }
      if (rawData?.basic?.autorenew?.enabled === false) {
        return 'manual';
      }
    } catch (e) {
      // continuer avec les fallbacks
    }
  }

  if (domainInfo.autoRenew === true) return 'automatic';
  if (domainInfo.autoRenew === false) return 'manual';

  return 'unknown';
};
